import {
  AloftPriceJSON,
  SensitivityFeatureKey,
  orUndefined,
  schAloftArea,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { selectMarketAdjustedSalePrices } from '../../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectMarketAdjustedSalePrice.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { useSensitivityContext } from '../PropertyGridSensitivityContext.tsx';
import { PropertyGridRow } from '../rows/PropertyGridRow.tsx';
import { PropertyGridField, PropertyGridSectionProps } from '../types.ts';

const featureFieldFields: Record<SensitivityFeatureKey, PropertyGridField> = {
  gla: {
    label: 'Gross Living Area',
    fieldName: 'editableData.improvements.gla',
    toDisplayValue: (v) => orUndefined(schAloftArea.parse)(v)?.getSiteAreaString(),
    toNumericValue: (v) => orUndefined(schAloftArea.parse)(v)?.getUnitAmount(),
    unit: 'sf',
  },
  bedrooms: {
    label: 'Bedrooms',
    fieldName: 'editableData.improvements.bedrooms',
    toDisplayValue: (v) => (typeof v === 'number' ? `${v} bd` : undefined),
    toNumericValue: (v) => (typeof v === 'number' ? v : undefined),
    unit: 'bd',
  },
  fullBaths: {
    label: 'Full Baths',
    fieldName: 'editableData.improvements.fullBaths',
    toDisplayValue: (v) => (typeof v === 'number' ? `${v} ba` : undefined),
    toNumericValue: (v) => (typeof v === 'number' ? v : undefined),
    unit: 'ba',
  },
  halfBaths: {
    label: 'Half Baths',
    fieldName: 'editableData.improvements.halfBaths',
    toDisplayValue: (v) => (typeof v === 'number' ? `${v} ba` : undefined),
    toNumericValue: (v) => (typeof v === 'number' ? v : undefined),
    unit: 'ba',
  },
  garageCarport: {
    label: 'Garage / Carport',
    // Use the full improvements object so we can sum garage + detachedGarage + carport spaces.
    fieldName: 'editableData.improvements',
    toDisplayValue: (v) => {
      type ImprovementsLike = {
        garage?: { spaces?: number };
        detachedGarage?: { spaces?: number };
        carport?: { spaces?: number };
      };
      const imp = v as ImprovementsLike | undefined;
      if (!imp) return undefined;
      const total =
        (imp.garage?.spaces ?? 0) + (imp.detachedGarage?.spaces ?? 0) + (imp.carport?.spaces ?? 0);
      return `${total} spc`;
    },
    toNumericValue: (v) => {
      type ImprovementsLike = {
        garage?: { spaces?: number };
        detachedGarage?: { spaces?: number };
        carport?: { spaces?: number };
      };
      const imp = v as ImprovementsLike | undefined;
      if (!imp) return undefined;
      return (
        (imp.garage?.spaces ?? 0) + (imp.detachedGarage?.spaces ?? 0) + (imp.carport?.spaces ?? 0)
      );
    },
    unit: 'spc',
  },
  siteSize: {
    label: 'Site Size',
    fieldName: 'editableData.site.area',
    toDisplayValue: (v) => orUndefined(schAloftArea.parse)(v)?.getSiteAreaString(),
    toNumericValue: (v) => orUndefined(schAloftArea.parse)(v)?.getUnitAmount(),
    unit: 'sf',
  },
  quality: {
    label: 'Quality',
    fieldName: 'editableData.improvements.uadQuality',
    toDisplayValue: (v) => (typeof v === 'number' ? `Q${v}` : undefined),
    toNumericValue: (v) => (typeof v === 'number' ? v : undefined),
    unit: '',
  },
  condition: {
    label: 'Condition',
    fieldName: 'editableData.improvements.uadCondition',
    toDisplayValue: (v) => (typeof v === 'number' ? `C${v}` : undefined),
    toNumericValue: (v) => (typeof v === 'number' ? v : undefined),
    unit: '',
  },
};

const salePriceField: PropertyGridField<AloftPriceJSON> = {
  fieldName: 'editableData.transaction.price',
  label: 'Sale Price',
  toDisplayValue: (v) => orUndefined(schAloftPrice.parse)(v)?.toString(true),
};

export const SensitivityUnitRateLookupSection = (props: PropertyGridSectionProps) => {
  const { fieldTypes } = props;
  const { sensitivityFeatureKey } = useSensitivityContext();
  const featureKey: SensitivityFeatureKey = sensitivityFeatureKey ?? 'gla';
  const variableField = featureFieldFields[featureKey];

  const differenceField: PropertyGridField = {
    ...variableField,
    label: 'Difference',
  };

  const isLabelColumn = fieldTypes.includes('label');
  const marketAdjustedPrices = useAppSelector(selectMarketAdjustedSalePrices);

  const adjustedSalePriceField: PropertyGridField = {
    fieldName: 'editableData.transaction.price',
    label: 'Adj. Sale Price',
    toDisplayValue: (_v, propertyId) => marketAdjustedPrices[propertyId]?.toString(true),
  };

  return (
    <>
      <PropertyGridRow
        field={salePriceField}
        {...props}
        fieldTypes={isLabelColumn ? ['label'] : ['readOnlyValue']}
      />
      <PropertyGridRow
        field={adjustedSalePriceField}
        {...props}
        fieldTypes={isLabelColumn ? ['label'] : ['readOnlyValue']}
      />
      <PropertyGridRow
        field={variableField}
        {...props}
        fieldTypes={isLabelColumn ? ['label'] : ['readOnlyValue']}
      />
      <PropertyGridRow
        field={differenceField}
        {...props}
        fieldTypes={isLabelColumn ? ['label'] : ['readOnlyDifference']}
      />
    </>
  );
};
